import type {
  Document,
  DocumentControlResponse,
  DocumentHistoryData,
  DocumentPrefix,
  DocumentRevision,
  DocumentRevisionConfig,
  DocumentRevisionNotification,
  MasterListResponse,
} from '@/entities/Document';
import type { ApiResponse } from '@/shared/lib';
import type { DocumentRevisionStatus } from '@/shared/lib';
import api from '@/shared/api';
import { withQuery } from '@/shared/lib';
import type { AxiosProgressEvent, AxiosResponse } from 'axios';
import * as mocks from '../model/mocks';

export interface DirectoryListingParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  term?: string;
  searchType?: 'name' | 'prefix';
  status?: string;
  revision?: string;
  hasTraining?: string;
  effectiveDate?: string;
}

export interface DocumentRevisionCode {
  documentRevisionId: number;
  code: string;
}

export interface RevisionByIdResponse {
  id: number;
  status: DocumentRevisionStatus;
  validityDate?: string;
  externalRevision?: string | null;
  config: DocumentRevisionConfig | null;
  physicalLocations: Array<{
    id: number;
    name: string;
    distribution: {
      quantity: number | null;
    };
  }>;
  notifications?: DocumentRevisionNotification | null;
  document: {
    id: number;
  };
}

let _pendingDocuments: DocumentRevision[] | null = null;

function getPendingDocuments(): DocumentRevision[] {
  if (!_pendingDocuments) {
    _pendingDocuments = mocks.PENDING_DOCUMENTS;
  }
  return _pendingDocuments;
}

class DocumentApi {
  getDocument(documentId: string): DocumentRevision | undefined {
    return getPendingDocuments().find(
      (document) => document.document?.id.toString() === documentId
    ) as DocumentRevision;
  }

  async getDocumentById(documentId: number): Promise<Document> {
    const response = await api.get<ApiResponse<Document>>(`/documents/${documentId}`);
    if (!response.data.data) {
      throw new Error('Document not found');
    }
    return response.data.data;
  }

  async getDocumentControl(revisionId: number): Promise<DocumentControlResponse> {
    const response = await api.get<ApiResponse<DocumentControlResponse>>(
      `/document-revisions/${revisionId}/control`
    );
    if (!response.data.data) {
      throw new Error('Document control data not found');
    }
    return response.data.data;
  }

  async getPdf(documentId: number): Promise<Blob> {
    const response = await api.get(`/documents/${documentId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async registerDocumentView(
    documentId: number,
    revisionId: number,
    basePath = 'documents'
  ): Promise<void> {
    await api.post(`/${basePath}/${documentId}/revisions/${revisionId}/view`);
  }

  async getDocumentHistory(documentId: number): Promise<DocumentHistoryData> {
    const response = await api.get<ApiResponse<DocumentHistoryData>>(
      `/documents/${documentId}/history`
    );
    const payload = response.data.data;
    return {
      requestDate: payload?.requestDate,
      revisions: Array.isArray(payload?.revisions) ? payload.revisions : [],
    };
  }

  async getDocumentPrefixes(): Promise<DocumentPrefix[]> {
    const response = await api.get('/documents/prefixes');
    return response.data.data || [];
  }

  async getAwaitingVerification(): Promise<DocumentRevision[]> {
    const response = await api.get('/document-revisions/awaiting-verification');
    return response.data.data || [];
  }

  async getMasterList(params: {
    type?: string;
    status?: string;
    prefix?: string;
    format?: string;
    startDate?: string;
    endDate?: string;
    generate?: string;
    view?: string;
  }): Promise<MasterListResponse> {
    const response = await api.get<MasterListResponse>(
      withQuery('/document-revisions/master-list', params)
    );

    return response.data;
  }

  async getAwaitingApprovalCount(): Promise<number> {
    try {
      const response = await api.get('/document-revisions/awaiting-approval/counter');
      return response.data.data?.count ?? 0;
    } catch {
      return 0;
    }
  }

  async getAwaitingVerificationCount(): Promise<number> {
    try {
      const response = await api.get('/document-revisions/awaiting-verification/counter');
      return response.data.data?.count ?? 0;
    } catch {
      return 0;
    }
  }

  async getAwaitingCpCount(): Promise<number> {
    try {
      const response = await api.get('/document-revisions/awaiting-cp/counter');
      return response.data.data?.count ?? 0;
    } catch {
      return 0;
    }
  }

  async deleteDocument(documentId: number): Promise<AxiosResponse<void>> {
    const response = await api.delete(`/documents/${documentId}`);
    return response;
  }

  async deleteDocumentRevision(revisionId: number): Promise<AxiosResponse<void>> {
    const response = await api.delete(`/document-revisions/${revisionId}`);
    return response;
  }

  async bulkDeleteDocumentRevisions(revisionIds: number[]): Promise<{
    totalRequested: number;
    successCount: number;
    failureCount: number;
    succeededIds: number[];
    failedOperations: Array<{ id: number; error: string }>;
    invalidIds: number[];
    fileOperationsFailed: boolean;
    warnings: string[];
  }> {
    const response = await api.delete<
      ApiResponse<{
        totalRequested: number;
        successCount: number;
        failureCount: number;
        succeededIds: number[];
        failedOperations: Array<{ id: number; error: string }>;
        invalidIds: number[];
        fileOperationsFailed: boolean;
        warnings: string[];
      }>
    >('/document-revisions/bulk', {
      data: { ids: revisionIds },
    });
    return response.data.data!;
  }

  async upload(
    formData: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    isExternal = false,
    externalDocumentName?: string
  ): Promise<AxiosResponse<ApiResponse<{ id: number }[]>>> {
    const shouldIncludeName =
      isExternal &&
      externalDocumentName !== undefined &&
      externalDocumentName !== null &&
      String(externalDocumentName).trim() !== '';
    const url = withQuery('/documents/upload', {
      external: isExternal ? true : undefined,
      documentName: shouldIncludeName ? externalDocumentName : undefined,
    });

    const response = await api.post<ApiResponse<{ id: number }[]>>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
      onUploadProgress,
    });

    return response;
  }

  async replaceRevisionUrl(
    documentId: number,
    revisionId: number,
    url: string
  ): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    const response = await api.put<ApiResponse<{ id: number }>>(
      `/documents/${documentId}/revisions/${revisionId}/replace-url`,
      { url }
    );
    return response;
  }

  async replaceRevision(
    documentId: number,
    revisionId: number,
    formData: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<AxiosResponse<ApiResponse<{ id: number }>>> {
    const response = await api.put<ApiResponse<{ id: number }>>(
      `/documents/${documentId}/revisions/${revisionId}/replace`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
        onUploadProgress,
      }
    );

    return response;
  }

  async link(payload: {
    urls: Array<{ url: string; name: string }>;
    changelogs?: string;
    managers?: string;
    external?: boolean;
    documentName?: string;
  }): Promise<AxiosResponse<ApiResponse<{ id: number }[]>>> {
    const response = await api.post<ApiResponse<{ id: number }[]>>('/documents/link', payload);
    return response;
  }

  async getDocumentRevisionsByPrefix(params: {
    prefixCode?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: 'asc' | 'desc';
    search?: string;
    status?: string;
  }): Promise<ApiResponse<DocumentRevision[]>> {
    const response = await api.get<ApiResponse<DocumentRevision[]>>('/documents/revisions', {
      params,
    });
    return response.data;
  }

  async getDocumentRevisionCodes(params: {
    prefixCode: string;
    status: string;
  }): Promise<DocumentRevisionCode[]> {
    const response = await api.get<ApiResponse<DocumentRevisionCode[]>>('/documents/revisions', {
      params: { ...params, paginate: false },
    });
    return response.data.data ?? [];
  }

  async getDocumentsByFolder(
    folderId: number,
    params: DirectoryListingParams
  ): Promise<ApiResponse<DocumentRevision[]>> {
    const response = await api.get<ApiResponse<DocumentRevision[]>>(
      `/documents/by-folder/${folderId}`,
      { params }
    );
    return response.data;
  }

  async getRevisionById(revisionId: number): Promise<RevisionByIdResponse> {
    const response = await api.get<ApiResponse<RevisionByIdResponse>>(
      `/document-revisions/${revisionId}`
    );
    if (!response.data.data) {
      throw new Error('Revisão não encontrada');
    }
    return response.data.data;
  }

  async getPreviousRevisions(
    documentId: number,
    excludeRevisionId: number
  ): Promise<DocumentRevision[]> {
    const response = await api.get<ApiResponse<DocumentRevision[]>>(
      `/document-revisions/document/${documentId}/previous-revisions`,
      { params: { excludeRevisionId } }
    );
    return response.data.data ?? [];
  }

  async getDocumentPreviousRevisions(
    documentId: number,
    excludeRevisionId: number
  ): Promise<DocumentRevision[]> {
    const response = await api.get<ApiResponse<DocumentRevision[]>>(
      `/documents/${documentId}/previous-revisions`,
      { params: { excludeRevisionId } }
    );
    return response.data.data ?? [];
  }
}

export const documentApi = new DocumentApi();

export function getDocument(documentId: string) {
  return documentApi.getDocument(documentId);
}

export async function getDocumentControl(revisionId: number): Promise<DocumentControlResponse> {
  return documentApi.getDocumentControl(revisionId);
}
